import React, { useState } from 'react';
import { CheckCircle, Zap, Settings, Wrench, HelpCircle, Eye } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.tsx';
import { cn } from '../lib/utils.ts';
import { TemplateRecommendation as TemplateRecommendationType } from '../types/index.ts';

interface TemplateRecommendationProps {
  recommendations: TemplateRecommendationType[];
  onTemplateSelected: (templateType: string) => void;
  isLoading?: boolean;
}

const templateIcons = {
  Quickstart: Zap,
  Setup: Settings,
  Configuration: Wrench,
  Troubleshooting: HelpCircle
};

const templateDescriptions = {
  Quickstart: 'Get users up and running quickly with essential steps and immediate value',
  Setup: 'Comprehensive initial setup and installation procedures',
  Configuration: 'Detailed configuration options and customization settings',
  Troubleshooting: 'Problem-solving guides for common issues and solutions'
};

export const TemplateRecommendation: React.FC<TemplateRecommendationProps> = ({
  recommendations,
  onTemplateSelected,
  isLoading = false
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState<string | null>(null);

  const handleTemplateSelect = (templateType: string) => {
    setSelectedTemplate(templateType);
  };

  const handleConfirmSelection = () => {
    if (selectedTemplate) {
      onTemplateSelected(selectedTemplate);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-100';
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getConfidenceText = (confidence: number) => {
    if (confidence >= 0.8) return 'High';
    if (confidence >= 0.6) return 'Medium';
    return 'Low';
  };

  if (isLoading) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle>Analyzing Content...</CardTitle>
          <CardDescription>
            Our AI is analyzing your documentation to recommend the best template type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-24 bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Template Recommendations</CardTitle>
        <CardDescription>
          Based on your content analysis, here are the recommended template types
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* AI Recommendations */}
        <div className="simpi-grid">
          {recommendations.map((rec) => {
            const Icon = templateIcons[rec.type];
            const isSelected = selectedTemplate === rec.type;
            const isRecommended = rec.confidence >= 0.7;
            
            return (
              <div key={rec.type} className="simpi-item">
                <div className="simpi-card-wrapper" onClick={() => handleTemplateSelect(rec.type)}>
                  <div className="simpi-card">
                    <div className="simpi-card-text">
                      {isRecommended && (
                        <div className="steps-label">
                          Recommended
                        </div>
                      )}
                      
                      <div className="flex items-start gap-3">
                        <Icon className="h-6 w-6 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="title">{rec.type}</h3>
                            <span className={cn(
                              'text-xs px-2 py-1 rounded-full',
                              getConfidenceColor(rec.confidence)
                            )}>
                              {getConfidenceText(rec.confidence)} ({Math.round(rec.confidence * 100)}%)
                            </span>
                          </div>
                          <p className="brand mb-2">
                            {templateDescriptions[rec.type]}
                          </p>
                          <p className="text-sm font-medium mb-2">
                            Why this template:
                          </p>
                          <p className="text-xs text-muted-foreground mb-3">
                            {rec.reasoning}
                          </p>
                          
                          <div className="flex gap-2">
                            <button
                              className={`simpi-button ${isSelected ? 'simpi-button-primary' : 'simpi-button-outline'}`}
                              onClick={() => handleTemplateSelect(rec.type)}
                            >
                              {isSelected ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Selected
                                </>
                              ) : (
                                'Select'
                              )}
                            </button>
                            <button
                              className="simpi-button simpi-button-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowPreview(showPreview === rec.type ? null : rec.type);
                              }}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Preview
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {showPreview === rec.type && (
                        <div className="mt-3 p-3 bg-white/80 border rounded text-sm">
                          <h4 className="font-medium mb-1">Preview:</h4>
                          <p className="text-muted-foreground">{rec.preview}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Manual Override Section */}
        <div className="border-t pt-4">
          <h4 className="font-medium mb-3">Or choose manually:</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(templateIcons).map(([type, Icon]) => (
              <Button
                key={type}
                variant={selectedTemplate === type ? 'default' : 'outline'}
                onClick={() => handleTemplateSelect(type)}
                className="flex flex-col h-auto p-4"
              >
                <Icon className="h-6 w-6 mb-2" />
                <span className="text-sm">{type}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Confirm Selection */}
        {selectedTemplate && (
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleConfirmSelection}
              size="lg"
              className="w-full max-w-md"
            >
              Generate {selectedTemplate} Guide
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};